import {FileOutlined, LockOutlined, MenuOutlined} from '@ant-design/icons';
import {Layout, Menu, message, Popconfirm, Space, Tabs, Typography, Drawer, Button, Card, Form, Input} from 'antd';
import {useNavigate} from 'react-router-dom';
import React, {useEffect, useState, lazy, Suspense} from 'react';
import http from "../framework/http.js";
import {observer} from "mobx-react-lite";
import useIsMobile from "../framework/MobileCheck";

import CategoryManagement from "./CategoryManagement.js";
import ModelManagement from "./ModelManagement.js";
import BlogManagement from "./BlogManagement.js";
import 'react-quill/dist/quill.snow.css';
import "./AdminPanel.css"

const menus = [
    {key: "1", label: "Category Management", icon: <FileOutlined/>},
    {key: "2", label: "Model Management", icon: <FileOutlined/>},
    {key: "3", label: "Blog Management", icon: <FileOutlined/>},
]

function Index() {
    const {Header, Sider, Content} = Layout;
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState();
    const [content, setContent] = useState([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const isMobile = useIsMobile();
    const [isTablet, setIsTablet] = useState(false);
    const [changePasswordDrawerOpen, setChangePasswordDrawerOpen] = useState(false);
    const [form] = Form.useForm();
    const [passwordUpdating, setPasswordUpdating] = useState(false);

    // Check if tablet on mount and resize
    useEffect(() => {
        const checkTablet = () => {
            setIsTablet(window.innerWidth <= 992 && window.innerWidth > 480);
        };

        checkTablet();
        window.addEventListener('resize', checkTablet);
        return () => window.removeEventListener('resize', checkTablet);
    }, []);

    useEffect(() => {
        const token = window.localStorage.getItem("token");
        if (!token) {
            // 未登录，跳转登录页面
            navigate("/login", {replace: true});
        } else {
            // 检查token是否过期，无效token会返回401在http的拦截器中被捕获导致跳转到登录页
            http.post("/loginCheck");
        }
    }, [navigate]);

    async function onLogoutConfirm() {
        const res = await http.post("/logout");
        if (res.data && res.data.code === 200) {
            window.localStorage.removeItem("token");
            message.success("logged out");
            navigate("/login", {replace: true});
        }
    }

    const onTabEdit = (targetKey) => {
        const newTabs = tabs.filter(items => items.key !== targetKey);
        setTabs(newTabs);
        setContent(content.filter(items => items.key !== targetKey))
        const currentStillExists = newTabs.some(item => item.key === activeTab);
        if (!currentStillExists && newTabs.length > 0) {
            setActiveTab(newTabs[newTabs.length - 1].key);
        }
    };

    const onMenuClick = (e) => {
        const menuFound = menus.find((o) => o.key === e.key);
        if (tabs.filter(item => item.key === menuFound.key).length > 0) {
            // if exist
            setActiveTab(menuFound.key);
        } else {
            // if not exist
            let page;
            if (menuFound.label === "Category Management") {
                page = <CategoryManagement/>
            } else if (menuFound.label === "Model Management") {
                page = <ModelManagement/>
            } else if (menuFound.label === "Blog Management") {
                page = <BlogManagement/>
            }
            setTabs([...tabs, {key: menuFound.key, label: menuFound.label}]);
            setContent([...content, {key: menuFound.key, component: page}])
            setActiveTab(menuFound.key);
        }
        // Close drawer on mobile after menu click
        if (isMobile || isTablet) {
            setDrawerVisible(false);
        }
    };

    const onTabChange = (e) => {
        setActiveTab(e);
    };

    const changePassword = async (formData) => {
        setPasswordUpdating(true);
        const res = await http.put("/changePassword", {
            oldPassword: formData.oldPassword,
            newPassword: formData.password
        });
        if (res.data?.code === 200) {
            form.resetFields();
            setChangePasswordDrawerOpen(false);
            message.success("Password changed successfully!");
        } else if (res.data?.code !== 401) {
            message.error(res.data?.msg || "Error, please try again later");
        }
        setPasswordUpdating(false);
    };

    return (
        <Layout className="admin-layout">
            <Header className="admin-header">
                {(isMobile || isTablet) && (
                    <Button
                        className="admin-menu-btn"
                        type="text"
                        icon={<MenuOutlined/>}
                        onClick={() => setDrawerVisible(true)}
                    />
                )}
                <Typography.Title level={isMobile ? 5 : 2} className="admin-header-title" editable={false}>
                    {isMobile ? 'Admin' : 'E-tech Admin Panel'}
                </Typography.Title>
                <Space size={isMobile ? "small" : "large"} className="admin-logout-space">
                    <span className="admin-operation-btn" onClick={()=>{setChangePasswordDrawerOpen(true)}}>Change Password</span>
                    <Popconfirm title="Sure to logout?" okText="confirm" cancelText="cancel"
                                onConfirm={onLogoutConfirm}>
                        <span className="admin-operation-btn">Logout</span>
                    </Popconfirm>
                </Space>
            </Header>

            {/* Mobile/Tablet Drawer Menu */}
            {(isMobile || isTablet) && (
                <Drawer
                    title="Admin Menu"
                    placement="left"
                    onClose={() => setDrawerVisible(false)}
                    open={drawerVisible}
                    className="admin-drawer"
                    width={isMobile ? '80%' : 280}
                    styles={{body: {padding: '10px'}}}
                >
                    <Menu
                        selectable={false}
                        mode="inline"
                        items={menus}
                        onClick={onMenuClick}
                        className="admin-drawer-menu"
                    />
                </Drawer>
            )}

            <Layout>
                {!isMobile && !isTablet && (
                    <Sider className="admin-sider" width={280} collapsible collapsed={collapsed} onCollapse={() => {
                        setCollapsed(!collapsed)
                    }} theme="light">
                        <Menu id="menu" selectable={false} mode="inline" items={menus} onClick={onMenuClick}/>
                    </Sider>
                )}
                <Content className="admin-content">
                    {tabs.length > 0 ?
                        (<>
                            <Tabs
                                type="editable-card"
                                hideAdd
                                items={tabs}
                                activeKey={activeTab}
                                onEdit={onTabEdit}
                                onChange={onTabChange}
                                className="admin-tabs"
                            />
                            {content.map(o =>
                                <div key={o.key}
                                     style={{display: o.key === activeTab ? 'block' : 'none'}}
                                     className="admin-tab-content">{o.component}</div>)}
                        </>)
                        :
                        (<div className="admin-empty-state"></div>)
                    }


                </Content>
            </Layout>
            <Drawer open={changePasswordDrawerOpen} onClose={() => {
                setChangePasswordDrawerOpen(false)
            }}>
                <Space direction={"vertical"} style={{width: "100%"}}>
                    <Card>
                        <Typography.Title level={4} style={{marginTop: 0}}>Change Password:</Typography.Title>
                        <Form form={form} layout="vertical" onFinish={changePassword}>
                            <Form.Item
                                label="Current password"
                                name="oldPassword"
                                hasFeedback
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input your current password!',
                                    },
                                    {
                                        min: 8,
                                        message: "Minimum length 8",
                                        validateTrigger: "onsubmit"
                                    }
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined className="site-form-item-icon"/>}
                                    type="password"
                                    placeholder="Current password"
                                />
                            </Form.Item>
                            <Form.Item
                                name="password"
                                label="New password"
                                hasFeedback
                                rules={[
                                    {
                                        required: true, message: 'Please input a password!',
                                    },
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.reject();
                                            }
                                            const invalidCharactersRegex = /[^A-Za-z\d@$!%*?&.]/;// only listed can be accepted
                                            const complexityRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*(\d|[@$!%*?&.]))/;// at least one upper,one lower and one number or special
                                            const minLen = 8;
                                            const maxLen = 32;

                                            if (invalidCharactersRegex.test(value)) {
                                                return Promise.reject(new Error('Password contains invalid characters. Only letters, numbers, and @$!%*?&. are allowed.'));
                                            }
                                            if (!complexityRegex.test(value)) {
                                                return Promise.reject(new Error('Password must contain at least one uppercase letter, one lowercase letter and at least one number or special character.'));
                                            }
                                            if (value.length < minLen) {
                                                return Promise.reject(new Error("Password must be at least " + minLen + " characters!"));
                                            }
                                            if (value.length > maxLen) {
                                                return Promise.reject(new Error("Password cannot be longer than " + maxLen + " characters"));
                                            }
                                            return Promise.resolve();
                                        },
                                    }]}
                            >
                                <Input.Password prefix={<LockOutlined className="site-form-item-icon"/>}
                                                placeholder="New password"/>
                            </Form.Item>
                            <Form.Item
                                name="confirmPassword"
                                label="Confirm new password"
                                dependencies={['password']}
                                hasFeedback
                                rules={[{
                                    required: true, message: 'Please confirm your password!',
                                }, ({getFieldValue}) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('The new password that you entered do not match!'));
                                    },
                                }),]}
                            >
                                <Input.Password prefix={<LockOutlined className="site-form-item-icon"/>}
                                                placeholder="Confirm new password"/>
                            </Form.Item>
                            <Form.Item style={{textAlign: "left"}}>
                                <Button type="primary" htmlType="submit" loading={passwordUpdating}>
                                    Submit
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Space>
            </Drawer>
        </Layout>
    );
}

export default observer(Index);
